'use client';

import Link from 'next/link';
import { useState } from 'react';
import { MarineIcon, type MarineIconName } from '@/components/MarineIcon';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    message: '',
    website: '' // honeypot — see the hidden field below
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subjectOptions = [
    'Project Registration',
    'Methodology Questions',
    'Media & Press Inquiry',
    'Partnership Opportunity',
    'Technical Support',
    'Other'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          website: formData.website,
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send message');
        setLoading(false);
        return;
      }

      setSubmitted(true);
      setLoading(false);
      setFormData({ name: '', email: '', organization: '', subject: '', message: '', website: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('An error occurred. Please try again later.');
      setLoading(false);
    }
  };

  const contactCards = [
    {
      icon: 'flow',
      title: 'Project Registration',
      desc: 'Questions about registering a new marine conservation project',
      action: 'project-registration'
    },
    {
      icon: 'methodology',
      title: 'Methodology Questions',
      desc: 'Technical inquiries about approved or proposed methodologies',
      action: 'methodology-questions'
    },
    {
      icon: 'transparency',
      title: 'Media & Press',
      desc: 'Press inquiries, media kits, and interview requests',
      action: 'media-press'
    },
    {
      icon: 'safeguards',
      title: 'Partnerships',
      desc: 'Strategic partnership and collaboration opportunities',
      action: 'partnerships'
    }
  ];

  const faqs = [
    {
      question: 'How do I register a new marine conservation project?',
      answer: 'Contact our Project Registration team with detailed information about your project. We\'ll guide you through the registration process, including baseline assessment, methodology selection, and project documentation requirements. No project has yet completed registration, so no timeline commitment has been adopted; ask us for the current expected pace.'
    },
    {
      question: 'What is the timeline for methodology approval?',
      answer: 'Methodology proposals go through completeness review, public comment and expert peer review under MCC-200. No adopted instrument sets a fixed review duration; ask us for the current expected pace for your submission.'
    },
    {
      question: 'Can existing projects apply retroactively?',
      answer: 'Retroactive registration is considered case by case; no adopted instrument sets a fixed retroactive-eligibility window. Retroactive credits follow the same validation requirements as any other project. Contact our team for the current eligibility criteria.'
    },
    {
      question: 'What fees are involved in project registration?',
      answer: 'No fee schedule has been adopted yet (MCC-FEES is still in draft, and the two fee positions the founder has decided are reserved to the Board or unpriced). Fees are not currently set. Contact our team for the current position on your project.'
    },
    {
      question: 'How are credits priced on the MCC registry?',
      answer: 'No credit has yet been issued or transacted, so there is no price or transaction data to show. The Programme itself sets no price for its units; pricing is a matter between the parties to a transaction.'
    },
    {
      question: 'Do you support international projects?',
      answer: 'Yes, we support marine conservation projects globally. However, projects must align with local regulations, international treaties, and sovereignty requirements. Contact us to discuss specific jurisdictional questions.'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--mcc-surface)' }}>
      <style>{`
        @media (max-width: 768px) {
          .cnt-nav-links { display: none !important; }
          .cnt-mobile-menu-btn { display: flex !important; }
          .cnt-nav-container { padding: 0 16px !important; height: 64px !important; }
          .cnt-subtitle { display: none !important; }
          .cnt-hero { padding: 60px 16px !important; }
          .cnt-hero h1 { font-size: 32px !important; }
          .cnt-hero p { font-size: 16px !important; }
          .cnt-quick-section { padding: 40px 16px !important; }
          .cnt-quick-section h2 { font-size: 28px !important; margin-bottom: 40px !important; }
          .cnt-grid { grid-template-columns: 1fr !important; }
          .cnt-contact-section { padding: 40px 16px !important; }
          .cnt-contact-section h2 { font-size: 28px !important; }
          .cnt-form-grid { grid-template-columns: 1fr !important; }
          .cnt-office-section { padding: 40px 16px !important; }
          .cnt-office-section h2 { font-size: 28px !important; margin-bottom: 40px !important; }
          .cnt-office-grid { grid-template-columns: 1fr !important; gap: 30px !important; }
          .cnt-faq-section { padding: 40px 16px !important; }
          .cnt-faq-section h2 { font-size: 28px !important; margin-bottom: 40px !important; }
          .cnt-faq { max-width: 100% !important; }
          .cnt-footer { padding: 30px 16px !important; font-size: 13px !important; }
        }
      `}</style>
      {/* Hero Section */}
      <section className="cnt-hero" style={{ backgroundColor: 'var(--mcc-navy)', color: 'white', padding: '80px 20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ fontSize: '56px', fontWeight: 600, marginBottom: '20px', letterSpacing: '-1px' }}>Get in Touch</h1>
          <p style={{ fontSize: '20px', maxWidth: '700px', margin: '0 auto', lineHeight: 1.6, opacity: 0.95 }}>Have questions about marine conservation projects, methodologies, or partnerships? We're here to help.</p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="cnt-quick-section" style={{ maxWidth: '1400px', margin: '0 auto', padding: '80px 20px', marginBottom: '80px' }}>
        <h2 style={{ fontSize: '36px', fontWeight: 600, marginBottom: '60px', color: 'var(--mcc-navy)', textAlign: 'center' }}>Quick Contact Options</h2>
        
        <div className="cnt-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px' }}>
          {contactCards.map((card, idx) => (
            <div
              key={idx}
              style={{
                backgroundColor: 'white',
                borderRadius: 0,
                padding: '40px 30px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: '1px solid var(--mcc-border)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
              }}
            >
              <div style={{ width: '48px', height: '48px', borderRadius: 0, backgroundColor: 'rgba(11,25,41,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'var(--mcc-navy)' }}>
                <MarineIcon name={card.icon as MarineIconName} size={24} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '12px' }}>{card.title}</h3>
              <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', lineHeight: 1.6, marginBottom: '20px' }}>{card.desc}</p>
              <a href={`#contact-form?type=${card.action}`} style={{
                display: 'inline-block',
                color: 'var(--mcc-navy)',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none',
                borderBottom: '2px solid var(--mcc-navy)',
                transition: 'color 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--mcc-sea)';
                e.currentTarget.style.borderBottomColor = 'var(--mcc-sea)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--mcc-navy)';
                e.currentTarget.style.borderBottomColor = 'var(--mcc-navy)';
              }}
              >
                Select this option →
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="cnt-contact-section" style={{ backgroundColor: 'white', padding: '80px 20px', marginBottom: '80px' }} id="contact-form">
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 600, marginBottom: '16px', color: 'var(--mcc-navy)' }}>Contact Us</h2>
            <p style={{ fontSize: '16px', color: 'var(--mcc-muted)', lineHeight: 1.6 }}>Fill out the form below and we'll respond within 2 business days.</p>
          </div>

          {submitted && (
            <div style={{
              backgroundColor: 'var(--mcc-success-bg)',
              border: '1px solid #4caf50',
              color: '#2e7d32',
              padding: '16px 20px',
              borderRadius: 0,
              marginBottom: '30px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Thank you for your message! We'll be in touch soon.
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              border: '1px solid #ef5350',
              color: '#c62828',
              padding: '16px 20px',
              borderRadius: 0,
              marginBottom: '30px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Name Field */}
            <div>
              <label htmlFor="contact-name" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--mcc-ink)', marginBottom: '8px' }}>Full Name *</label>
              <input
                id="contact-name"
                type="text"
                name="name"
                autoComplete="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '15px',
                  border: '1px solid var(--mcc-border)',
                  borderRadius: 0,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: 'var(--mcc-surface)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-navy)';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-border)';
                  e.currentTarget.style.backgroundColor = 'var(--mcc-surface)';
                }}
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="contact-email" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--mcc-ink)', marginBottom: '8px' }}>Email Address *</label>
              <input
                id="contact-email"
                type="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '15px',
                  border: '1px solid var(--mcc-border)',
                  borderRadius: 0,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: 'var(--mcc-surface)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-navy)';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-border)';
                  e.currentTarget.style.backgroundColor = 'var(--mcc-surface)';
                }}
              />
            </div>

            {/* Organization Field */}
            <div>
              <label htmlFor="contact-organization" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--mcc-ink)', marginBottom: '8px' }}>Organization</label>
              <input
                id="contact-organization"
                type="text"
                name="organization"
                autoComplete="organization"
                value={formData.organization}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '15px',
                  border: '1px solid var(--mcc-border)',
                  borderRadius: 0,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: 'var(--mcc-surface)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-navy)';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-border)';
                  e.currentTarget.style.backgroundColor = 'var(--mcc-surface)';
                }}
              />
            </div>

            {/* Subject Dropdown */}
            <div>
              <label htmlFor="contact-subject" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--mcc-ink)', marginBottom: '8px' }}>Subject *</label>
              <select
                id="contact-subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '15px',
                  border: '1px solid var(--mcc-border)',
                  borderRadius: 0,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: 'var(--mcc-surface)',
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%2310233A%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%3e%3c/polyline%3e%3c/svg%3e")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '20px',
                  paddingRight: '36px'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-navy)';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-border)';
                  e.currentTarget.style.backgroundColor = 'var(--mcc-surface)';
                }}
              >
                <option value="">Select a subject...</option>
                {subjectOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="contact-message" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--mcc-ink)', marginBottom: '8px' }}>Message *</label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '15px',
                  border: '1px solid var(--mcc-border)',
                  borderRadius: 0,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: 'var(--mcc-surface)',
                  resize: 'vertical'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-navy)';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mcc-border)';
                  e.currentTarget.style.backgroundColor = 'var(--mcc-surface)';
                }}
              />
            </div>

            {/* Honeypot: off-screen, not reachable by keyboard, hidden from screen
                readers. Humans never fill it; generic form bots fill everything.
                A filled value quarantines the submission server-side without
                telling the sender, so a bot learns nothing from being caught. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <label htmlFor="c-website">Leave this field empty</label>
              <input
                id="c-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website}
                onChange={handleInputChange}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '14px 32px',
                backgroundColor: 'var(--mcc-navy)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                border: 'none',
                borderRadius: 0,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: loading ? 0.7 : 1,
                marginTop: '10px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = 'var(--mcc-sea)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = 'var(--mcc-navy)';
                }
              }}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* Office Information Section */}
      <section className="cnt-office-section" style={{ backgroundColor: 'white', padding: '80px 20px', marginBottom: '80px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: 600, marginBottom: '60px', color: 'var(--mcc-navy)', textAlign: 'center' }}>Office & Contact Information</h2>

          <div className="cnt-office-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '50px' }}>
            {/* Norway Office */}
            <div>
              <h3 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '24px' }}>Norway Headquarters</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Address</p>
                  <p style={{ fontSize: '16px', color: 'var(--mcc-ink)', lineHeight: 1.6 }}>
                    Langelandsvegen 35<br />
                    6018 Ålesund<br />
                    Norway
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Email</p>
                  <a href="mailto:info@marineconservationcredits.com" style={{ fontSize: '16px', color: 'var(--mcc-navy)', textDecoration: 'none', transition: 'color 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--mcc-sea)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--mcc-navy)';
                  }}
                  >
                    info@marineconservationcredits.com
                  </a>
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Timezone</p>
                  <p style={{ fontSize: '16px', color: 'var(--mcc-ink)' }}>CET (UTC+1) / CEST (UTC+2)</p>
                </div>
              </div>
            </div>

            {/* General Inquiries */}
            <div>
              <h3 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '24px' }}>General Inquiries</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Primary Email</p>
                  <a href="mailto:info@marineconservationcredits.com" style={{ fontSize: '16px', color: 'var(--mcc-navy)', textDecoration: 'none', transition: 'color 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--mcc-sea)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--mcc-navy)';
                  }}
                  >
                    info@marineconservationcredits.com
                  </a>
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Response Time</p>
                  <p style={{ fontSize: '16px', color: 'var(--mcc-ink)' }}>2 business days (typical)</p>
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Languages</p>
                  <p style={{ fontSize: '16px', color: 'var(--mcc-ink)' }}>English (other languages by arrangement)</p>
                </div>
              </div>
            </div>

            {/* Office Hours */}
            <div>
              <h3 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '24px' }}>Office Hours</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Monday - Friday</p>
                  <p style={{ fontSize: '16px', color: 'var(--mcc-ink)' }}>9:00 AM - 5:00 PM CET</p>
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Saturday - Sunday</p>
                  <p style={{ fontSize: '16px', color: 'var(--mcc-ink)' }}>Closed</p>
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--mcc-muted)', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>Holidays</p>
                  <p style={{ fontSize: '16px', color: 'var(--mcc-ink)' }}>Norwegian public holidays</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="cnt-faq-section" style={{ maxWidth: '1400px', margin: '0 auto', padding: '80px 20px', marginBottom: '80px' }}>
        <h2 style={{ fontSize: '36px', fontWeight: 600, marginBottom: '60px', color: 'var(--mcc-navy)', textAlign: 'center' }}>Frequently Asked Questions</h2>

        <div className="cnt-faq" style={{ display: 'grid', gap: '20px', maxWidth: '900px', margin: '0 auto' }}>
          {faqs.map((faq, idx) => (
            <FAQItem key={idx} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </section>

    </div>
  );
}

// FAQ Item Component
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: '1px solid var(--mcc-border)',
        borderRadius: 0,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        if (!expanded) {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
        }
      }}
      onMouseLeave={(e) => {
        if (!expanded) {
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '24px',
          backgroundColor: expanded ? 'var(--mcc-surface)' : 'white',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.3s ease',
          fontFamily: 'inherit'
        }}
        onMouseEnter={(e) => {
          if (!expanded) {
            e.currentTarget.style.backgroundColor = 'var(--mcc-surface)';
          }
        }}
        onMouseLeave={(e) => {
          if (!expanded) {
            e.currentTarget.style.backgroundColor = 'white';
          }
        }}
      >
        <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--mcc-navy)' }}>{question}</span>
        <span
          style={{
            fontSize: '20px',
            color: 'var(--mcc-navy)',
            transition: 'transform 0.3s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            minWidth: '24px',
            textAlign: 'center'
          }}
        >
          ▼
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '0 24px 24px', borderTop: '1px solid var(--mcc-border)', backgroundColor: 'white' }}>
          <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', lineHeight: 1.7, margin: 0 }}>{answer}</p>
        </div>
      )}
    </div>
  );
}
